import type { ReactNode } from 'react'
import { AlertTriangle, Ban, FileQuestion, WifiOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import type { AppErrorCode } from '@/services/api/errors'

const ICONS: Partial<Record<AppErrorCode | 'default', typeof AlertTriangle>> = {
  NETWORK: WifiOff,
  FORBIDDEN: Ban,
  NOT_FOUND: FileQuestion,
  default: AlertTriangle,
}

export interface ErrorStateProps {
  title?: string
  description?: string
  code?: AppErrorCode | 'default'
  actionLabel?: string
  onAction?: () => void
  children?: ReactNode
  className?: string
}

/** Reusable inline / page error surface */
export function ErrorState({
  title = 'Something went wrong',
  description = 'Please try again.',
  code = 'default',
  actionLabel,
  onAction,
  children,
  className,
}: ErrorStateProps) {
  const Icon = ICONS[code] ?? ICONS.default!

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border border-border/60 bg-card/80 px-6 py-12 text-center shadow-card ring-1 ring-black/[0.04] dark:ring-white/[0.06]',
        className,
      )}
      role="alert"
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <p className="mt-1.5 max-w-md text-sm text-muted-foreground">{description}</p>
      {children}
      {actionLabel && onAction ? (
        <Button className="mt-5" onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  )
}
